import { DependencyList, RefObject, useLayoutEffect, useRef } from 'react';
import { getOverflowAncestors } from '@floating-ui/react';

type UseAutoUpdate = (
    enabled: boolean,
    callbackFn: () => void,
    resizeObserverDeps: RefObject<Element>,
    dependencies: DependencyList,
) => void;

export const useAutoUpdate: UseAutoUpdate = (enabled, callbackFn, resizeObserverDeps, dependencies) => {
    const callbackFnRef = useRef(callbackFn);

    useLayoutEffect(() => {
        if (enabled) {
            let frameId: number | null = null;

            const scheduleUpdate = () => {
                if (frameId === null) {
                    frameId = requestAnimationFrame(() => {
                        frameId = null;
                        callbackFnRef.current();
                    });
                }
            };

            const resizeObserver = new ResizeObserver(scheduleUpdate);
            resizeObserverDeps?.current && resizeObserver.observe(resizeObserverDeps?.current);

            scheduleUpdate();

            const ancestors =
                resizeObserverDeps?.current instanceof Node ? getOverflowAncestors(resizeObserverDeps?.current) : [];

            ancestors.forEach((dep) => {
                dep.addEventListener('scroll', scheduleUpdate);
                dep.addEventListener('resize', scheduleUpdate);
            });

            return () => {
                if (frameId !== null) {
                    cancelAnimationFrame(frameId);
                }
                resizeObserver.disconnect();
                ancestors.forEach((dep) => {
                    dep.removeEventListener('scroll', scheduleUpdate);
                    dep.removeEventListener('resize', scheduleUpdate);
                });
            };
        }
    }, dependencies);
};
